import { useEffect, useState } from "react";
import { api, type SchoolEvent } from "./api";
import { OWNER_ONLY_NOTE } from "./features";
import { Close } from "./icons";
import { useMe } from "./me";

// The CHA school calendar (decision 47), kept by the teacher. Student projects read it with
// getData("school"); this is where it's filled in: paste events as simple lines, or import a
// calendar link (.ics, e.g. a Google Calendar's iCal address). Each save replaces the whole list.

const EXAMPLE = `2026-11-25 to 2026-11-27 | Thanksgiving break | no school
2026-12-04 | Basketball vs. Collegiate | game
2026-12-11 | Christmas program | event`;

const asLines = (events: SchoolEvent[]) => events.map((e) => `${e.date}${e.endDate ? ` to ${e.endDate}` : ""} | ${e.time ? `${e.time} ` : ""}${e.title} | ${e.kind}`).join("\n");

export function SchoolCalendarEntry() {
  const owner = !!useMe()?.user.owner; // co-teachers see it; only an owner changes it
  const [events, setEvents] = useState<SchoolEvent[] | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    void api.schoolCalendar().then(
      (r) => {
        setEvents(r.events);
        setLink(r.url || null);
      },
      () => setEvents(null),
    );
  }, []);
  if (!events) return null;
  const today = new Date().toISOString().slice(0, 10);
  const next = events.find((e) => (e.endDate ?? e.date) >= today);
  return (
    <section aria-label="School calendar" className="subs-entry">
      <div>
        <h2>School calendar</h2>
        <p className="muted small">
          {events.length === 0
            ? 'Empty. Fill it in and students can use it in projects with getData("school").'
            : `${events.length} event${events.length === 1 ? "" : "s"}${next ? ` · next: ${next.title} (${next.date})` : ""}${link ? " · syncs from your calendar link" : ""} · students read it with getData("school")`}
        </p>
        {!owner && <p className="muted small owner-only-note">{OWNER_ONLY_NOTE}</p>}
      </div>
      {owner && (
        <button type="button" className="outline" onClick={() => setOpen(true)}>
          Edit calendar
        </button>
      )}
      {open && <SchoolCalendarDialog events={events} onSaved={setEvents} onClose={() => setOpen(false)} />}
    </section>
  );
}

function SchoolCalendarDialog({ events, onSaved, onClose }: { events: SchoolEvent[]; onSaved: (e: SchoolEvent[]) => void; onClose: () => void }) {
  const [text, setText] = useState(asLines(events));
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const save = async (body: { text?: string; url?: string }) => {
    setBusy(true);
    setNote(null);
    try {
      const r = await api.setSchoolCalendar(body);
      onSaved(r.events);
      setText(asLines(r.events));
      setUrl("");
      setNote(`Saved ${r.events.length} event${r.events.length === 1 ? "" : "s"}.${r.skipped.length ? ` Skipped ${r.skipped.length} line${r.skipped.length === 1 ? "" : "s"} it couldn't read: ${r.skipped.slice(0, 3).join(" · ")}` : ""}`);
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="testlab-backdrop" role="dialog" aria-modal="true" aria-label="School calendar">
      <div className="testlab school-cal">
        <div className="row between">
          <div>
            <span className="eyebrow">Private data source · getData("school")</span>
            <h2>School calendar</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            <Close size={16} />
          </button>
        </div>
        <p className="hint">One event per line: date (or “date to date”) | title | kind. Kinds like “no school”, “game”, “chapel” or “event” help students filter. Only BuildLab users can read this.</p>
        <textarea value={text} placeholder={EXAMPLE} rows={12} spellCheck={false} onChange={(e) => setText(e.target.value)} aria-label="Events, one per line" />
        <div className="row">
          <button type="button" className="primary small" disabled={busy} onClick={() => void save({ text })}>
            Save events
          </button>
        </div>
        <div className="row">
          <input type="url" value={url} placeholder="Or import a calendar link (.ics / webcal://)" onChange={(e) => setUrl(e.target.value)} aria-label="Calendar link" />
          <button type="button" className="outline small" disabled={busy || !url.trim()} onClick={() => void save({ url })}>
            Import
          </button>
        </div>
        {note && <p className="ink2 small">{note}</p>}
      </div>
    </div>
  );
}
